<template>
    <div class="grade-analysis">
        <!-- 顶部筛选卡片 -->
        <el-card class="filter-card">
            <el-form inline>
                <el-form-item label="学期">
                    <el-select v-model="filters.semesterId" placeholder="选择学期" style="width: 150px">
                        <el-option v-for="s in semesters" :key="s.id" :label="s.name" :value="s.id" />
                    </el-select>
                </el-form-item>
                <el-form-item label="考试类型">
                    <el-select v-model="filters.examType" placeholder="选择考试" style="width: 160px" allow-create filterable>
                        <el-option-group label="常规考试">
                            <el-option label="月考1" value="monthly_1" />
                            <el-option label="期中考试" value="midterm" />
                            <el-option label="月考2" value="monthly_2" />
                            <el-option label="期末考试" value="final" />
                        </el-option-group>
                        <el-option-group label="高三模拟">
                            <el-option label="一模" value="mock_1" />
                            <el-option label="二模" value="mock_2" />
                            <el-option label="三模" value="mock_3" />
                        </el-option-group>
                        <el-option-group label="其他">
                            <el-option label="月考3" value="monthly_3" />
                            <el-option label="诊断性考试" value="diagnostic" />
                        </el-option-group>
                    </el-select>
                </el-form-item>
                <el-form-item>
                    <el-button type="primary" :icon="Search" @click="fetchOverview" :loading="overviewLoading"
                        :disabled="!filters.semesterId || !filters.examType">
                        查询总览
                    </el-button>
                </el-form-item>
            </el-form>
        </el-card>

        <!-- 主内容 Tabs -->
        <el-tabs v-model="activeTab" class="main-tabs" type="border-card">
            <!-- Tab 1: 数据总览 -->
            <el-tab-pane name="overview">
                <template #label>
                    <span class="tab-label"><el-icon><DataAnalysis /></el-icon> 数据总览</span>
                </template>

                <div v-if="overviewLoading" class="loading-wrap">
                    <el-skeleton :rows="8" animated />
                </div>

                <el-empty v-else-if="!hasOverviewData" description="请先选择学期和考试类型，点击「查询总览」" />

                <div v-else class="overview-content">
                    <div v-for="(gradeData, gradeName) in overviewData" :key="gradeName" class="grade-section">
                        <!-- 年级标题 -->
                        <div class="grade-header">
                            <div class="grade-title-row">
                                <span class="grade-badge">{{ gradeName }}</span>
                                <div class="grade-stats">
                                    <el-tag type="info" size="small">共 {{ gradeData.total_students }} 名学生</el-tag>
                                    <el-tag type="success" size="small">{{ gradeData.classes.length }} 个班级</el-tag>
                                    <el-tag type="warning" size="small">年级总均分 {{ gradeData.grade_total_avg }}</el-tag>
                                </div>
                            </div>
                        </div>

                        <!-- 班级对比表 -->
                        <el-table :data="overviewTableData(gradeData)" border stripe
                            :header-cell-style="{ background: '#f3f4f6', color: '#111827', fontWeight: 'bold' }"
                            :row-class-name="overviewRowClass" style="width: 100%">
                            <el-table-column prop="class_name" label="班级" width="90" fixed align="center">
                                <template #default="{ row }">
                                    <span :class="row.isGradeAvg ? 'grade-avg-label' : ''">
                                        {{ row.class_name }}
                                    </span>
                                </template>
                            </el-table-column>
                            <el-table-column prop="student_count" label="人数" width="70" align="center">
                                <template #default="{ row }">
                                    <span>{{ row.isGradeAvg ? '—' : row.student_count }}</span>
                                </template>
                            </el-table-column>
                            <el-table-column v-for="subj in gradeData.subjects" :key="subj" :label="subj" align="center">
                                <el-table-column label="均分" min-width="80" align="center">
                                    <template #default="{ row }">
                                        <div class="subj-cell">
                                            <div v-if="!row.isGradeAvg && row.teachers && row.teachers[subj]" class="teacher-name">
                                                {{ row.teachers[subj] }}
                                            </div>
                                            <div :class="subjectCellClass(row, subj, gradeData)" class="subj-score">
                                                {{ row.averages[subj] ?? '—' }}
                                            </div>
                                        </div>
                                    </template>
                                </el-table-column>
                                <el-table-column label="排名" width="65" align="center">
                                    <template #default="{ row }">
                                        <span v-if="row.isGradeAvg" class="dash">—</span>
                                        <div v-else-if="row.subject_ranks && row.subject_ranks[subj]" class="subj-rank">
                                            {{ row.subject_ranks[subj] }}
                                        </div>
                                        <span v-else class="dash">—</span>
                                    </template>
                                </el-table-column>
                            </el-table-column>
                            <el-table-column prop="sanke_avg" label="三总均分" width="90" align="center">
                                <template #default="{ row }">
                                    <strong>{{ row.sanke_avg ?? '—' }}</strong>
                                </template>
                            </el-table-column>
                            <el-table-column prop="wuke_avg" label="五总均分" width="90" align="center">
                                <template #default="{ row }">
                                    <strong>{{ row.wuke_avg ?? '—' }}</strong>
                                </template>
                            </el-table-column>
                            <el-table-column prop="total_avg" label="总均分" width="90" align="center">
                                <template #default="{ row }">
                                    <strong>{{ row.total_avg }}</strong>
                                </template>
                            </el-table-column>
                            <el-table-column prop="rank" label="年级排名" width="90" align="center">
                                <template #default="{ row }">
                                    <span v-if="row.isGradeAvg" class="dash">—</span>
                                    <el-tag v-else-if="row.rank === 1" type="warning" effect="dark" size="small">🥇 第1</el-tag>
                                    <el-tag v-else-if="row.rank === 2" effect="plain" size="small" style="color:#9ca3af;border-color:#d1d5db">🥈 第2</el-tag>
                                    <el-tag v-else-if="row.rank === 3" type="danger" effect="plain" size="small">🥉 第3</el-tag>
                                    <span v-else class="rank-num">第 {{ row.rank }}</span>
                                </template>
                            </el-table-column>
                        </el-table>
                    </div>
                </div>
            </el-tab-pane>

            <!-- Tab 2: 学生排名 -->
            <el-tab-pane name="rankings">
                <template #label>
                    <span class="tab-label"><el-icon><Trophy /></el-icon> 学生排名</span>
                </template>

                <el-card class="ranking-config-card" shadow="never">
                    <el-form inline>
                        <el-form-item label="年级">
                            <el-select v-model="rankingFilters.grade" placeholder="选择年级" style="width: 110px"
                                @change="onGradeChange">
                                <el-option label="高一" value="高一" />
                                <el-option label="高二" value="高二" />
                                <el-option label="高三" value="高三" />
                            </el-select>
                        </el-form-item>
                        <el-form-item label="班级">
                            <el-select v-model="rankingFilters.className" placeholder="全年级" clearable
                                style="width: 110px">
                                <el-option v-for="c in availableClasses" :key="c" :label="c" :value="c" />
                            </el-select>
                        </el-form-item>
                    </el-form>

                    <!-- 科目组合预设 -->
                    <div class="preset-section">
                        <span class="preset-label">快速预设：</span>
                        <div class="preset-buttons">
                            <el-button v-for="p in presets" :key="p.key" size="small"
                                :type="activePreset === p.key ? 'primary' : 'default'"
                                @click="applyPreset(p)">
                                {{ p.label }}
                            </el-button>
                        </div>
                    </div>

                    <!-- 科目配置 -->
                    <div class="subject-config-grid">
                        <div v-for="sc in subjectConfigs" :key="sc.subject" class="subject-config-item"
                            :class="{ disabled: !sc.enabled }">
                            <el-checkbox v-model="sc.enabled" @change="activePreset = 'custom'">
                                <span class="subj-name">{{ sc.subject }}</span>
                            </el-checkbox>
                            <div class="weight-control">
                                <span class="weight-label">×</span>
                                <el-input-number v-model="sc.weight" :min="0.1" :max="2" :step="0.5"
                                    :precision="1" size="small" style="width: 80px" :disabled="!sc.enabled"
                                    @change="activePreset = 'custom'" />
                            </div>
                        </div>
                    </div>

                    <div class="config-actions">
                        <el-button type="primary" :icon="DataAnalysis" @click="fetchRankings"
                            :loading="rankingLoading"
                            :disabled="!filters.semesterId || !filters.examType || !rankingFilters.grade">
                            计算排名
                        </el-button>
                        <el-button :icon="Download" @click="exportRankings"
                            :disabled="!rankingData || rankingData.total_count === 0"
                            :loading="exporting">
                            导出 Excel
                        </el-button>
                        <span v-if="rankingData" class="result-count">
                            共 {{ rankingData.total_count }} 名学生
                        </span>
                    </div>
                </el-card>

                <!-- 排名表格 -->
                <el-card v-if="rankingData && rankingData.students.length > 0" shadow="never" class="ranking-table-card">
                    <el-table :data="rankingData.students" border stripe max-height="600"
                        :header-cell-style="{ background: '#f3f4f6', color: '#111827', fontWeight: 'bold' }"
                        row-key="student_no">
                        <el-table-column prop="rank" label="排名" width="70" fixed align="center">
                            <template #default="{ row }">
                                <el-tag v-if="row.rank === 1" type="warning" effect="dark" size="small">🥇 1</el-tag>
                                <el-tag v-else-if="row.rank === 2" effect="plain" size="small" style="color:#6b7280;border-color:#d1d5db">🥈 2</el-tag>
                                <el-tag v-else-if="row.rank === 3" type="danger" effect="plain" size="small">🥉 3</el-tag>
                                <span v-else class="rank-num">{{ row.rank }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="student_no" label="学号" width="110" align="center" />
                        <el-table-column prop="name" label="姓名" width="90" align="center" />
                        <el-table-column prop="class_name" label="班级" width="80" align="center" />
                        <el-table-column v-for="subj in rankingData.subjects" :key="subj" :label="subjectLabel(subj)"
                            width="85" align="center">
                            <template #default="{ row }">
                                {{ row.subject_scores[subj] ?? '—' }}
                            </template>
                        </el-table-column>
                        <el-table-column prop="weighted_total" label="加权总分" width="100" fixed="right" align="center">
                            <template #default="{ row }">
                                <strong class="total-score">{{ row.weighted_total }}</strong>
                            </template>
                        </el-table-column>
                    </el-table>
                </el-card>

                <el-empty v-else-if="rankingData && rankingData.students.length === 0"
                    description="该筛选条件下没有成绩数据" />
            </el-tab-pane>
        </el-tabs>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { Search, DataAnalysis, Download, Trophy } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { semesterApi, gradeAnalysisApi } from '@/api'

// ============ 公共状态 ============
const semesters = ref([])
const activeTab = ref('overview')

const filters = reactive({
    semesterId: null,
    examType: null
})

const allSubjects = ['语文', '数学', '英语', '物理', '化学', '生物', '政治', '历史', '地理']

// ============ 数据总览 ============
const overviewLoading = ref(false)
const overviewData = ref(null)

const hasOverviewData = computed(() => overviewData.value && Object.keys(overviewData.value).length > 0)

const fetchOverview = async () => {
    if (!filters.semesterId || !filters.examType) return
    overviewLoading.value = true
    try {
        overviewData.value = await gradeAnalysisApi.getOverview(filters.semesterId, filters.examType)
        if (!hasOverviewData.value) {
            ElMessage.warning('该条件下暂无成绩数据')
        }
    } catch (e) {
        console.error(e)
    } finally {
        overviewLoading.value = false
    }
}

// 构建表格数据：班级行 + 年级均分行
const overviewTableData = (gradeData) => {
    const rows = gradeData.classes.map(c => ({ ...c, isGradeAvg: false }))
    rows.push({
        class_name: '年级均分',
        averages: gradeData.grade_averages,
        total_avg: gradeData.grade_total_avg,
        sanke_avg: gradeData.grade_sanke_avg,
        wuke_avg: gradeData.grade_wuke_avg,
        student_count: gradeData.total_students,
        rank: null,
        isGradeAvg: true
    })
    return rows
}

const overviewRowClass = ({ row }) => row.isGradeAvg ? 'grade-avg-row' : ''

// 高于年级均分时显示绿色
const subjectCellClass = (row, subj, gradeData) => {
    if (row.isGradeAvg) return 'grade-avg-cell'
    const val = row.averages[subj]
    const gradeAvg = gradeData.grade_averages[subj]
    if (val === undefined || gradeAvg === undefined) return ''
    return val >= gradeAvg ? 'above-avg' : 'below-avg'
}

// ============ 学生排名 ============
const rankingLoading = ref(false)
const exporting = ref(false)
const rankingData = ref(null)
const activePreset = ref('sanke')

const rankingFilters = reactive({
    grade: '高一',
    className: null
})

// 从总览数据中提取可用班级
const availableClasses = computed(() => {
    if (!overviewData.value || !overviewData.value[rankingFilters.grade]) return []
    return overviewData.value[rankingFilters.grade].classes.map(c => c.class_name)
})

const onGradeChange = () => {
    rankingFilters.className = null
    rankingData.value = null
}

// 科目配置列表
const subjectConfigs = reactive(
    allSubjects.map(s => ({ subject: s, weight: 1.0, enabled: false }))
)

// 预设
const presets = [
    { key: 'sanke', label: '语数外', subjects: ['语文', '数学', '英语'], weights: {} },
    { key: 'wuke', label: '五科（物化折半）', subjects: ['语文', '数学', '英语', '物理', '化学'], weights: { '物理': 0.5, '化学': 0.5 } },
    { key: 'allsub', label: '全科', subjects: allSubjects, weights: {} },
    { key: 'custom', label: '自定义', subjects: null, weights: {} }
]

const applyPreset = (preset) => {
    activePreset.value = preset.key
    if (preset.subjects === null) return // 自定义不修改配置
    subjectConfigs.forEach(sc => {
        sc.enabled = preset.subjects.includes(sc.subject)
        sc.weight = preset.weights[sc.subject] ?? 1.0
    })
}

// 科目列标题（权重不为1时显示系数）
const subjectLabel = (subj) => {
    const sc = subjectConfigs.find(s => s.subject === subj)
    if (!sc || sc.weight === 1.0) return subj
    return `${subj}×${sc.weight}`
}

const fetchRankings = async () => {
    if (!filters.semesterId || !filters.examType || !rankingFilters.grade) {
        ElMessage.warning('请先选择学期、考试类型和年级')
        return
    }
    const enabledConfigs = subjectConfigs.filter(sc => sc.enabled)
    if (enabledConfigs.length === 0) {
        ElMessage.warning('请至少选择一个科目')
        return
    }
    rankingLoading.value = true
    try {
        rankingData.value = await gradeAnalysisApi.getStudentRankings({
            semester_id: filters.semesterId,
            exam_type: filters.examType,
            grade: rankingFilters.grade,
            class_name: rankingFilters.className || null,
            subject_configs: subjectConfigs.map(sc => ({
                subject: sc.subject,
                weight: sc.weight,
                enabled: sc.enabled
            }))
        })
        if (rankingData.value.total_count === 0) {
            ElMessage.warning('该条件下暂无成绩数据')
        }
    } catch (e) {
        console.error(e)
    } finally {
        rankingLoading.value = false
    }
}

const exportRankings = async () => {
    if (!rankingData.value || rankingData.value.total_count === 0) return
    exporting.value = true
    try {
        const blob = await gradeAnalysisApi.exportStudentRankings({
            semester_id: filters.semesterId,
            exam_type: filters.examType,
            grade: rankingFilters.grade,
            class_name: rankingFilters.className || null,
            subject_configs: subjectConfigs.map(sc => ({
                subject: sc.subject,
                weight: sc.weight,
                enabled: sc.enabled
            }))
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `成绩排名_${rankingFilters.grade}.xlsx`
        link.click()
        window.URL.revokeObjectURL(url)
        ElMessage.success('导出成功')
    } catch (e) {
        console.error(e)
    } finally {
        exporting.value = false
    }
}

// ============ 初始化 ============
onMounted(async () => {
    try {
        semesters.value = await semesterApi.getAll()
        // 默认选中当前学期
        const cur = await semesterApi.getCurrent().catch(() => null)
        if (cur) filters.semesterId = cur.id
    } catch (e) {
        console.error(e)
    }
    // 默认预设：语数外
    applyPreset(presets[0])
})
</script>

<style scoped>
.grade-analysis {
    max-width: 1600px;
}

.filter-card {
    margin-bottom: 16px;
}

.main-tabs {
    border-radius: 12px;
    overflow: hidden;
}

.tab-label {
    display: flex;
    align-items: center;
    gap: 6px;
}

/* ---- 总览 ---- */
.overview-content {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.grade-section {
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    overflow: hidden;
}

.grade-header {
    background: linear-gradient(90deg, #4f46e5 0%, #7c3aed 100%);
    padding: 12px 16px;
}

.grade-title-row {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.grade-badge {
    font-size: 16px;
    font-weight: 700;
    color: white;
    padding: 2px 12px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 20px;
}

.grade-stats {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.grade-avg-label {
    font-weight: 600;
    color: #4f46e5;
}

.above-avg {
    color: #059669;
    font-weight: 600;
}

.below-avg {
    color: #dc2626;
}

.grade-avg-cell {
    font-weight: 600;
    color: #4f46e5;
}

.rank-num {
    font-size: 13px;
    color: #6b7280;
}

.dash {
    color: #d1d5db;
}

.subj-cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 4px 0;
}

.teacher-name {
    font-size: 12px;
    color: #9ca3af;
    line-height: 1;
}

.subj-score {
    font-size: 14px;
    line-height: 1;
}

.subj-rank {
    display: inline-block;
    font-size: 13px;
    font-weight: 600;
    color: #c2410c;
    background: #ffedd5;
    border: 1px solid #fed7aa;
    padding: 2px 8px;
    border-radius: 6px;
    line-height: 1.3;
}


/* ---- 排名 ---- */
.ranking-config-card {
    margin-bottom: 16px;
    background: #fafafa;
}

.preset-section {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 12px 0;
    flex-wrap: wrap;
}

.preset-label {
    font-size: 13px;
    color: #6b7280;
    white-space: nowrap;
}

.preset-buttons {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.subject-config-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 10px;
    padding: 14px;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    margin-bottom: 14px;
}

.subject-config-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    background: #f9fafb;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
    transition: all 0.2s;
}

.subject-config-item:not(.disabled):hover {
    border-color: #a5b4fc;
    background: #eff6ff;
}

.subject-config-item.disabled {
    opacity: 0.45;
}

.subj-name {
    font-weight: 500;
    color: #374151;
}

.weight-control {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-left: auto;
}

.weight-label {
    font-size: 13px;
    color: #9ca3af;
}

.config-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.result-count {
    font-size: 13px;
    color: #6b7280;
}

.ranking-table-card {
    margin-top: 0;
}

.total-score {
    font-size: 15px;
    color: #4f46e5;
}

.loading-wrap {
    padding: 20px;
}

/* 总览年级均分行特殊样式（通过全局 CSS 添加） */
</style>

<style>
/* 成绩分析 - 年级均分行高亮（需穿透 scoped） */
.grade-avg-row td.el-table__cell {
    background: #eff6ff !important;
    font-weight: 600;
}
</style>

